import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Play, Pause, Zap, OctagonX, PlayCircle, Check, X, Send, Loader2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MODES = [["assistita", "Assistita", "propone tutto, approvi tu"], ["supervisionata", "Supervisionata", "esegue solo avvisi interni, il resto lo approvi"], ["avanzata", "Avanzata", "come supervisionata, entro i limiti impostati"]];
const RISK = { ordinary: ["Ordinaria", "bg-slate-100 text-slate-600"], sensitive: ["Email reale", "bg-amber-100 text-amber-800"], financial: ["Finanziaria", "bg-rose-100 text-rose-700"] };
const STAT_LABELS = { prenotazioni_oggi: "Prenotazioni oggi", richieste_domiciliari_aperte: "Domiciliari in attesa", nuovi_iscritti_24h: "Nuovi iscritti 24h", operatori_da_approvare: "Operatori da approvare", richieste_mediche_aperte: "Richieste mediche aperte", azioni_ai_da_approvare: "Azioni AI da approvare", email_ricevute_24h: "Email ricevute 24h", annunci_da_moderare: "Annunci da moderare", segnalazioni_cafe: "Segnalazioni Café" };

async function call(fn, ok, reload) {
  try {
    const r = await fn();
    if (r?.data?.skipped) toast.info(r.data.skipped);
    else toast.success(ok);
    reload();
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function AgentCard({ a, reload }) {
  const act = (x, ok) => call(() => api.post(`/ai/agents/${a.key}/${x}`), ok, reload);
  return (
    <div className={`rounded-2xl border p-4 ${a.configured ? "border-slate-200 bg-white" : "border-dashed border-slate-300 bg-slate-50"}`} data-testid={`ai-agent-${a.key}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><Bot className="h-4 w-4 text-emerald-600" />{a.name}</p>
        <span data-testid={`ai-agent-status-${a.key}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${a.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{a.configured ? (a.enabled ? "Attivo" : "In pausa") : "Non configurato"}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{a.description}</p>
      {a.last_run && <p className="mt-1 text-[11px] text-slate-400">Ultima esecuzione: {fmtDateTime(a.last_run)}</p>}
      {a.configured && (
        <div className="mt-3 flex gap-2">
          {a.enabled
            ? <Button size="sm" variant="outline" data-testid={`ai-agent-pause-${a.key}`} onClick={() => act("pause", "Agente in pausa")}><Pause className="mr-1 h-3.5 w-3.5" />Pausa</Button>
            : <Button size="sm" variant="outline" data-testid={`ai-agent-start-${a.key}`} onClick={() => act("start", "Agente avviato")}><Play className="mr-1 h-3.5 w-3.5" />Avvia</Button>}
          <Button size="sm" variant="outline" data-testid={`ai-agent-run-${a.key}`} onClick={() => act("run", "Esecuzione completata")}><Zap className="mr-1 h-3.5 w-3.5" />Esegui ora</Button>
        </div>
      )}
    </div>
  );
}

export function Approvals({ items, reload }) {
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="ai-approvals-empty">Nessuna attività in attesa di approvazione.</p>;
  return items.map((t) => (
    <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3" data-testid={`ai-task-${t.id}`}>
      <div>
        <p className="text-sm font-semibold text-slate-900">{t.title}</p>
        <p className="text-[11px] text-slate-500">{t.agent} · {fmtDateTime(t.created_at)} <span className={`ml-1 rounded-full px-2 py-0.5 font-bold ${RISK[t.risk][1]}`}>{RISK[t.risk][0]}</span></p>
        {t.payload?.preview && <p className="mt-1 max-w-2xl whitespace-pre-wrap text-xs text-slate-600" data-testid={`ai-task-preview-${t.id}`}>{t.payload.preview}</p>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" data-testid={`ai-task-approve-${t.id}`} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => call(() => api.post(`/ai/approvals/${t.id}/approve`), "Attività approvata ed eseguita", reload)}><Check className="mr-1 h-3.5 w-3.5" />Approva</Button>
        <Button size="sm" variant="outline" data-testid={`ai-task-reject-${t.id}`} className="text-rose-700" onClick={() => call(() => api.post(`/ai/approvals/${t.id}/reject`), "Attività rifiutata", reload)}><X className="mr-1 h-3.5 w-3.5" />Rifiuta</Button>
      </div>
    </div>
  ));
}

function DirectorChat() {
  const [msg, setMsg] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/ai/director/chat", { message: msg });
      setLog((l) => [...l, { id: `${l.length}q`, q: msg, a: data.answer }]);
      setMsg("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="ai-director-chat">
      {log.map((m) => <div key={m.id} className="space-y-1 text-sm"><p className="font-semibold text-slate-800">Tu: {m.q}</p><p className="whitespace-pre-wrap rounded-xl bg-emerald-50 p-3 text-slate-700" data-testid="ai-director-answer">{m.a}</p></div>)}
      <form onSubmit={send} className="flex gap-2">
        <Input data-testid="ai-director-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Chiedi al Director AI (solo consigli, non esegue azioni)" />
        <Button data-testid="ai-director-send" disabled={busy || !msg.trim()} className="bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => <div className="space-y-2"><p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{title}</p>{children}</div>;

export function AIControlCenter() {
  const [o, setO] = useState(null);
  const load = useCallback(() => api.get("/ai/overview").then(({ data }) => setO(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
  }, [load]);
  if (!o) return null;
  const s = o.settings;
  const set = (body, ok) => call(() => api.post("/ai/settings", body), ok, load);
  return (
    <div className="space-y-6" data-testid="ai-control-center">
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 ${s.stopped ? "bg-rose-50" : "bg-emerald-50"}`}>
        <p className="text-sm font-semibold text-slate-800" data-testid="ai-global-status">{s.stopped ? "Tutte le automazioni sono ARRESTATE. Nessun agente agisce finché non le riattivi." : "Automazioni attive secondo le regole qui sotto."}</p>
        {s.stopped
          ? <Button data-testid="ai-resume-all" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => set({ stopped: false }, "Automazioni riattivate")}><PlayCircle className="mr-1 h-4 w-4" />Riattiva automazioni</Button>
          : <Button data-testid="ai-stop-all" className="bg-rose-600 hover:bg-rose-700" onClick={() => set({ stopped: true }, "Tutte le automazioni arrestate")}><OctagonX className="mr-1 h-4 w-4" />Arresta tutte le automazioni</Button>}
      </div>
      <Section title="Modalità di autonomia">
        <div className="grid gap-2 sm:grid-cols-3">
          {MODES.map(([k, l, d]) => <button key={k} data-testid={`ai-mode-${k}`} onClick={() => set({ mode: k }, `Modalità ${l}`)} className={`rounded-xl border p-3 text-left transition-colors ${s.mode === k ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}><p className="text-sm font-bold">{l}</p><p className="text-xs text-slate-500">{d}</p></button>)}
        </div>
        <p className="text-[11px] text-slate-500">In ogni modalità: decisioni cliniche solo umane, email reali e operazioni finanziarie sempre con approvazione, nessun movimento di denaro. Limiti: {s.max_actions_per_run} azioni per esecuzione, {o.llm_calls_today}/{s.max_llm_calls_per_day} chiamate AI oggi.</p>
      </Section>
      <Section title="Stato della clinica">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{Object.entries(o.stats).map(([k, v]) => <div key={k} className="rounded-xl bg-white p-3" data-testid={`ai-stat-${k}`}><p className="text-xl font-extrabold text-slate-900">{v}</p><p className="text-[11px] text-slate-500">{STAT_LABELS[k] || k}</p></div>)}</div>
      </Section>
      <Section title="Agenti"><div className="grid gap-3 md:grid-cols-2">{o.agents.map((a) => <AgentCard key={a.key} a={a} reload={load} />)}</div></Section>
      <Section title={`Da approvare (${o.approvals.length})`}><Approvals items={o.approvals} reload={load} /></Section>
      {o.report && <Section title={`Ultimo report · ${fmtDateTime(o.report.at)}`}><p className="whitespace-pre-wrap rounded-xl bg-white p-3 text-sm text-slate-700" data-testid="ai-last-report">{o.report.text}</p></Section>}
      <Section title="Chat con il Director AI"><DirectorChat /></Section>
      <Section title="Registro attività AI">
        <div className="max-h-72 space-y-1 overflow-y-auto rounded-xl bg-white p-3 text-xs" data-testid="ai-activity-log">
          {o.activity.map((x) => <p key={x.id}><span className="text-slate-400">{fmtDateTime(x.at)}</span> · <b>{x.agent}</b> · {x.action} {x.detail && `– ${x.detail}`}</p>)}
          {!o.activity.length && <p className="text-slate-500">Nessuna attività registrata.</p>}
        </div>
      </Section>
    </div>
  );
}
